/**
 * Application Routes
 *
 * Defines all routes for the application. The route tree is split at the
 * top level between the teacher host (scimodeling.com) and the student
 * join host (join.scimodeling.com) — see `src/lib/host.ts`. Both hosts are
 * served by the same bundle; only the router differs.
 */

import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { RootLayout } from '@/components/layout/root-layout';
import { AuthLayout } from '@/components/layout/auth-layout';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { AuthGuard, AdminGuard } from './guards';
import { JoinLayout, CodeRedirect } from './join-layout';
import { RouteErrorPage } from './route-error';
import { features } from '@/config/features';
import { isJoinHost } from '@/lib/host';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('@/pages/home'));
const LoginPage = lazy(() => import('@/pages/login'));
const RegisterPage = lazy(() => import('@/pages/register'));
const ForgotPasswordPage = lazy(() => import('@/pages/forgot-password'));
const ResetPasswordPage = lazy(() => import('@/pages/reset-password'));
const AuthConfirmPage = lazy(() => import('@/pages/auth-confirm'));
const OnboardingPage = lazy(() => import('@/pages/onboarding'));
const DashboardPage = lazy(() => import('@/pages/dashboard'));
const AdminSubmissionsPage = lazy(() => import('@/pages/admin-submissions'));
const AdminUsersPage = lazy(() => import('@/pages/admin-users'));
const LivePage = lazy(() => import('@/pages/live'));
const SessionSharesPage = lazy(() => import('@/pages/session-shares'));
const ModelSetupPage = lazy(() => import('@/pages/model-setup'));
const StudentSessionSharesPage = lazy(() => import('@/pages/student-session-shares'));
const NotFoundPage = lazy(() => import('@/pages/not-found'));
const BrowsePage = lazy(() => import('@/pages/browse'));
const PrivacyPage = lazy(() => import('@/pages/privacy'));
const TermsPage = lazy(() => import('@/pages/terms'));
const ContactPage = lazy(() => import('@/pages/contact'));
const SharePage = lazy(() => import('@/pages/share'));
const JoinPage = lazy(() => import('@/pages/join'));

// Models
const BallRampModel = lazy(() => import('@/pages/models/ball-ramp'));
const ConvectionSnakeModel = lazy(() => import('@/pages/models/convection-snake'));
const MagnetDropModel = lazy(() => import('@/pages/models/magnet-drop'));
const MagnetLaunchModel = lazy(() => import('@/pages/models/magnet-launch'));
const BatteryTrainModel = lazy(() => import('@/pages/models/battery-train'));
const PropertiesElectricalForcesModel = lazy(
  () => import('@/pages/models/properties-electrical-forces.tsx')
);
const HappySadBallModel = lazy(() => import('@/pages/models/happy-sad-ball'));
const CarOnRampModel = lazy(() => import('@/pages/models/car-on-ramp'));
const SolarCatModel = lazy(() => import('@/pages/models/solar-cat'));
const RulerDropModel = lazy(() => import('@/pages/models/ruler-drop'));

/**
 * Public routes - accessible without authentication
 */
const publicRoutes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'browse',
        element: <BrowsePage />,
      },
      {
        path: 'privacy',
        element: <PrivacyPage />,
      },
      {
        path: 'terms',
        element: <TermsPage />,
      },
      {
        path: 'contact',
        element: <ContactPage />,
      },
      {
        path: 'models/ball-ramp',
        element: <BallRampModel />,
      },
      {
        path: 'models/convection-snake',
        element: <ConvectionSnakeModel />,
      },
      {
        path: 'models/magnet-drop',
        element: <MagnetDropModel />,
      },
      {
        path: 'models/magnet-launch',
        element: <MagnetLaunchModel />,
      },
      {
        path: 'models/battery-train',
        element: <BatteryTrainModel />,
      },
      {
        path: 'models/properties-electrical-forces',
        element: <PropertiesElectricalForcesModel />,
      },
      {
        path: 'models/happy-sad-ball',
        element: <HappySadBallModel />,
      },
      {
        path: 'models/car-on-ramp',
        element: <CarOnRampModel />,
      },
      {
        path: 'models/solar-cat',
        element: <SolarCatModel />,
      },
      {
        path: 'models/ruler-drop',
        element: <RulerDropModel />,
      },
      {
        path: 'share/:code',
        element: <SharePage />,
      },
    ],
  },
];

/**
 * Auth routes - login, register, etc.
 */
const authRoutes: RouteObject[] = features.auth
  ? [
      {
        path: '/',
        element: <AuthLayout />,
        errorElement: <RouteErrorPage />,
        children: [
          {
            path: 'login',
            element: <LoginPage />,
          },
          {
            path: 'register',
            element: <RegisterPage />,
          },
          {
            path: 'forgot-password',
            element: <ForgotPasswordPage />,
          },
          {
            path: 'reset-password',
            element: <ResetPasswordPage />,
          },
          {
            path: 'auth/confirm',
            element: <AuthConfirmPage />,
          },
          {
            path: 'onboarding',
            element: (
              <AuthGuard>
                <OnboardingPage />
              </AuthGuard>
            ),
          },
        ],
      },
    ]
  : [];

/**
 * Protected routes - require authentication
 */
const protectedRoutes: RouteObject[] = features.auth
  ? [
      {
        path: '/dashboard',
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        errorElement: <RouteErrorPage />,
        children: [
          {
            index: true,
            element: <DashboardPage />,
          },
          {
            path: 'submissions',
            element: (
              <AdminGuard>
                <AdminSubmissionsPage />
              </AdminGuard>
            ),
          },
          {
            path: 'users',
            element: (
              <AdminGuard>
                <AdminUsersPage />
              </AdminGuard>
            ),
          },
          {
            path: 'live/:code',
            element: <LivePage />,
          },
          {
            path: 'shares/:code',
            element: <SessionSharesPage />,
          },
          {
            path: 'shares/:code/:shareId',
            element: <SessionSharesPage />,
          },
          {
            path: 'setup/:code',
            element: <ModelSetupPage />,
          },
        ],
      },
    ]
  : [
      // When auth is disabled, dashboard is public
      {
        path: '/dashboard',
        element: <DashboardLayout />,
        errorElement: <RouteErrorPage />,
        children: [
          {
            index: true,
            element: <DashboardPage />,
          },
          {
            path: 'submissions',
            element: <AdminSubmissionsPage />,
          },
          {
            path: 'users',
            element: <AdminUsersPage />,
          },
          {
            path: 'live/:code',
            element: <LivePage />,
          },
          {
            path: 'shares/:code',
            element: <SessionSharesPage />,
          },
          {
            path: 'shares/:code/:shareId',
            element: <SessionSharesPage />,
          },
          {
            path: 'setup/:code',
            element: <ModelSetupPage />,
          },
        ],
      },
    ];

/**
 * Catch-all route for 404
 */
const catchAllRoute: RouteObject = {
  path: '*',
  element: <NotFoundPage />,
};

/**
 * Join-host routes (join.scimodeling.com) — students only. No marketing,
 * no auth, no dashboard. Reuses the existing /share/:code route so the
 * models themselves are completely unmodified.
 */
const joinRoutes: RouteObject[] = [
  {
    path: '/',
    element: <JoinLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <JoinPage /> },
      { path: 'share/:code', element: <SharePage /> },
      { path: 'share/:code/shared', element: <StudentSessionSharesPage /> },
      { path: 'share/:code/shared/:shareId', element: <StudentSessionSharesPage /> },
      { path: ':code', element: <CodeRedirect /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];

/**
 * All application routes
 */
export const routes: RouteObject[] = isJoinHost()
  ? joinRoutes
  : [...publicRoutes, ...authRoutes, ...protectedRoutes, catchAllRoute];

export default routes;
